import React from "react";
import { useNavigate } from "react-router-dom";
import WarningIcon from "@mui/icons-material/Warning";
import { useAuth } from "../contexts/AuthContext";

/**
 * Always-visible top-of-page banner shown when the tenant's subscription is `past_due`.
 *
 * Lives at the route layer (rendered above all protected routes) so users get the same warning regardless of which page they happen to be on when a write is blocked. The billing-specific banner inside `BillingManagement.tsx` continues to render on that page; this one is the global counterpart.
 */
const GlobalBillingBanner: React.FC = () => {
  const { isAuthenticated, tenantFeatures, user } = useAuth();
  const navigate = useNavigate();

  // Only show for authenticated, non-system tenants in past_due. The system tenant has subscription_status === null and must never see this.
  if (!isAuthenticated || !user) return null;
  if (tenantFeatures.subscription_status !== "past_due") return null;

  const isAdmin = user.role === "admin" || user.is_superadmin;

  // Avoid showing on the deactivated page (different concern) and on the billing page itself (the page already has its own banner; double-banner is noise).
  const path = window.location.pathname;
  const search = window.location.search;
  const isOnBilling =
    path === "/admin" && new URLSearchParams(search).get("tab") === "billing";
  const isOnDeactivated = path === "/account-deactivated";
  if (isOnBilling || isOnDeactivated) return null;

  return (
    <div className="yellow-banner !border-t-0">
      <WarningIcon style={{ fontSize: 18 }} />
      <span>
        <strong>Payment past due.</strong> Your account is in read-only mode
        until the payment method is updated.
      </span>
      {isAdmin && (
        <button
          onClick={() => navigate("/admin?tab=billing")}
          style={{
            background: "none",
            border: "none",
            color: "#6d28d9",
            fontWeight: 600,
            cursor: "pointer",
            textDecoration: "underline",
            fontSize: 14,
            padding: 0,
          }}
        >
          Update payment
        </button>
      )}
    </div>
  );
};

export default GlobalBillingBanner;
